// eslint-disable-next-line no-redeclare
import angular from 'angular';
import { bus, interfaceLoaded } from '@hotjar/webapp/utils/events';
import { getApiHost } from '@hotjar/common';
import { ONBOARDING } from '@hotjar/webapp/constants/routes/onboarding';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { browserHistory } from '@hotjar/webapp/utils/routing/browser-history';
import { getAccessTokenHeaders, checkAndReloadAccessToken, getTargetUrl } from '@hotjar/auth';
import { isDemoSiteId, redirectToDemoLogin } from '@hotjar/webapp/domains/acquisition/demo/demo-site-redirections';
import { SITE } from '@hotjar/webapp/constants/routes';
import { LOGIN, LOGOUT } from '@hotjar/webapp/constants/routes/auth';
import { NO_SITE_ACCESS } from '@hotjar/webapp/constants/routes/status';

const migratedPathsSegmentIdentifier = [
    '/highlights',
    '/dashboard',
    '/funnels',
    '/onboarding',
    '/overview',
    '/heatmap',
    '/settings/sso',
    '/settings/user-attributes',
    '/settings/integrations',
    '/login',
    '/login/sso',
    '/logout',
    '/surveys/list',
    '/surveys/responses',
    '/surveys/results',
    '/surveys/performance',
    '/surveys/create',
    '/surveys/edit',
    '/feedback/create',
    '/feedback/edit',
    '/profile/details',
    '/profile/password',
    '/profile/notifications',
    '/profile/referrals',
    '/profile/email',
    '/survey-templates',
    '/setup',
    '/settings/account',
    '/settings/api',
    '/settings/subscriptions',
    '/settings/payment-details',
    '/settings/user-lookup',
    '/team/list',
    '/team/invite',
    '/checkout',
    '/site/list',
    '/site/create-organization',
    /\/site\/.*\/settings/,
    '/trends',
    '/playbacks'
];

const isMigratedToReact = (currentLocationUrl) =>
    migratedPathsSegmentIdentifier.find((path) => {
        if (path instanceof RegExp) {
            return path.test(currentLocationUrl);
        }
        return currentLocationUrl.includes(path);
    });

/*globals getBasedOnEnvironment:true */
angular.module('hjApp', [
    'hjApp.tracking',
    'ngSentry',
    'angulartics',
    'angulartics.google.analytics',
    'ipCookie',
    'restangular',
    'ui.bootstrap',
    'ui.router',
    'hjFeedbackModule',
    'hjSurveysModule',
    'hjProfileModule',
    'hjSettingsModule',
    'hjSiteModule',
    'ui.select',
    'ngSanitize',
    'jm.i18next',
    'wu.masonry',
    'hjApp.react',
    'hjApp.revenue',
    'hjApp.account',
    'hjApp.api',
    'hjApp.logging',
    'hjApp.models',
    'hjApp.plans',
    'hjApp.segments',
    'hjApp.buttons',
    'hjApp.storage',
    'hjApp.ui',
    'hjApp.user',
    'hjApp.utils',
    'hjApp.forms'
]);

/**
 * Set up an interceptor for all rest calls to:
 *  1. Start up the Loader spinner to show when requests are pending.
 *  2. Close Loader spinner once all requests are fulfilled.
 *  3. Show maintenance screen, if request is returned as being under maintenance.
 *  4. Handle 40x errors.
 */
angular
    .module('hjApp')
    .factory('restInterceptor', [
        '$q',
        '$rootScope',
        'Loader',
        function ($q, $rootScope, Loader) {
            let requestCounter = 0;

            function increment() {
                requestCounter += 1;
                if (requestCounter === 1) {
                    Loader.show('Loading...', true);
                }
            }

            function decrement() {
                if (requestCounter > 0) {
                    requestCounter -= 1;
                }

                // Hide the loader only once all requests have returned.
                if (requestCounter === 0) {
                    Loader.hide();
                }
            }

            return {
                request: function (config) {
                    increment();
                    return config;
                },

                requestError: function (res) {
                    decrement();
                    return $q.reject(res);
                },

                response: function (res) {
                    // We handle app-wide maintenance mode with a 200 that has maintenance: true.
                    if (res.status === 200 && res.data && res.data.maintenance) {
                        window.location = '/';
                    }
                    decrement();
                    return res;
                },

                responseError: function (res) {
                    decrement();

                    if (res.status === 401) {
                        $rootScope.$emit('unauthorized');
                    }

                    return $q.reject(res);
                }
            };
        }
    ])
    .factory('jwtInterceptor', [
        function () {
            return {
                request: async function (config) {
                    const tokenResult = await checkAndReloadAccessToken();
                    const accessTokenHeaders = getAccessTokenHeaders(tokenResult);
                    if (accessTokenHeaders) {
                        config.headers = { ...config.headers, ...accessTokenHeaders };
                    }
                    return config;
                }
            };
        }
    ])
    .config([
        '$compileProvider',
        '$stateProvider',
        '$urlRouterProvider',
        '$httpProvider',
        '$locationProvider',
        'RestangularProvider',
        '$provide',
        function (
            $compileProvider,
            $stateProvider,
            $urlRouterProvider,
            $httpProvider,
            $locationProvider,
            RestangularProvider,
            $provide
        ) {
            // Attach the previously declared rest interceptor.
            $httpProvider.interceptors.push('restInterceptor');
            $httpProvider.interceptors.push('jwtInterceptor');

            $httpProvider.useApplyAsync(true);
            $httpProvider.xsrfWhitelistedOrigins.push(getApiHost());
            $httpProvider.defaults.withCredentials = true;

            $compileProvider.debugInfoEnabled(getBasedOnEnvironment({ dev: true, test: true, default: false }));

            $locationProvider.html5Mode(true);

            $urlRouterProvider
                .when('/', [
                    '$q',
                    '$location',
                    async function () {
                        console.warn('>>> $urlRouterProvider triggered');
                    }
                ])
                .otherwise(function ($injector, $location) {
                    const currentLocationUrl = $location.url();
                    const $state = $injector.get('$state');
                    if (!isMigratedToReact(currentLocationUrl)) {
                        $state.go('404');
                    }
                    return currentLocationUrl;
                });

            RestangularProvider.setBaseUrl(getApiHost() + '/api/v1');

            $provide.decorator('$exceptionHandler', [
                '$delegate',
                function ($delegate) {
                    return function (exception, cause) {
                        if (typeof exception !== 'object') {
                            console.warn(exception); // eslint-disable-line no-console
                            return;
                        }
                        exception.cause = cause;
                        if (typeof hj !== 'undefined' && hj.exceptions) {
                            hj.exceptions.log(exception, 'Insights', cause);
                        }
                        $delegate(exception, cause);
                    };
                }
            ]);
        }
    ]);

angular.module('hjApp.api').factory('RestangularV2', function (Restangular) {
    return Restangular.withConfig(function (RestangularConfigurer) {
        RestangularConfigurer.setBaseUrl(getApiHost() + '/api/v2');
    });
});
angular.module('hjApp.api').factory('RestangularV3', function (Restangular) {
    return Restangular.withConfig(function (RestangularConfigurer) {
        RestangularConfigurer.setBaseUrl(getApiHost() + '/api/v3');
    });
});
angular.module('hjApp.api').factory('RestangularAsk', function (Restangular) {
    return Restangular.withConfig(function (RestangularConfigurer) {
        RestangularConfigurer.setBaseUrl(getApiHost() + '/api/ask/v1');
    });
});
angular
    .module('hjApp')
    .run(function (
        $rootScope,
        $location,
        $state,
        $timeout,
        $interval,
        Campaigns,
        restDefaultHandlers,
        UserData,
        Notify,
        Track,
        User
    ) {
        /**
         * *WARNING*: This is a super hack.
         *
         * Reason: In many places, we pass an angular function into a react component and we don’t call $digest
         * after react has called that function. As a result no change would appear to the user without this hack.
         *
         * History: Originally the angularJS `Quora` service set up an `$interval` that checked to see if the Quora
         * script had loaded. The script only ever loaded on one or two pages but the `$interval` incidentally ran
         * almost infinitely. This meant that calling angular functions from react worked without needing to call
         * `$digest` (or similar) to trigger updates in the angular code. The Quora service has since been changed to
         * use a regular interval but it was too late and too many places relies on that. We've set up this $interval
         * here to mimic what was happening in the Quora service.
         *
         * Action: Identifying areas where $digest would be needed is super difficult. It will get easier once the react
         * migration progresses
         */
        $interval(() => null, 100);

        // On 401 we log the user out.
        // NOTE (react-migration): This is already handled by setAxiosInterceptors.
        $rootScope.$on('unauthorized', function () {
            return browserHistory.replace(`${LOGOUT.path}/?next=${window.location.pathname}`);
        });

        // On 403 or 404 we show the item-unavailable state.
        // NOTE (react-migration): I don't think these will be needed. They currently aren't handled in react,
        // and no one seems to be missing them. Also handling 403 in a global way is not ideal.
        window.addEventListener('forbidden-notfound', () => $state.go('item-unavailable'));
        window.addEventListener('demo-account-forbidden', () => Notify.error('Forbidden for demo account'));

        // Check if any trial extensions should be applied.
        Campaigns.initTrialExtensions();

        // Reload user data on page refresh.
        if (UserData.loggedIn && !($location.search().demo || UserData.isDemoAccount())) {
            $rootScope.fetchingUserDataStatus = 'FETCHING';
            UserData.refreshFromServer(() => {
                $rootScope.fetchingUserDataStatus = 'COMPLETE';
                $rootScope.$broadcast('interfaceLoaded');
                bus.publish(interfaceLoaded());
            });
        }

        // Sets up the router to report state changes.
        $rootScope.$on('$stateChangeSuccess', function (event, toState, _, fromState) {
            Track.stateChange($location.url(), fromState, toState);

            const historyUrl = browserHistory.location.pathname + browserHistory.location.search;
            const windowUrl = window.location.pathname + window.location.search;

            // Don't push to the history stack if there is no url associated with the new state
            // or if the URL is the same in both the browser history and in the window
            if (toState.url && historyUrl !== windowUrl) {
                // We need to do this to trigger a change in the React app since it doesn't watch the URL.
                browserHistory.replace(windowUrl);
            }

            // Give views time to render before reloading widgets
            // (surveys need DOM to be ready, 1 tick is enough)
            $timeout(function () {
                if (typeof hj !== 'undefined') {
                    hj('stateChange', $location.url());
                }
            }, 0);
        });

        /**
         * Register a listener to watch for state transitions, intercept and re-route if necessary.
         */
        $rootScope.$on('$stateChangeStart', function (event, toState, toParams) {
            // Anonymous users can only access pages with requiredAccess == 'none', otherwise be redirected to /login.
            if (!UserData.loggedIn) {
                if (toState.requiredAccess !== 'none') {
                    event.preventDefault();
                    Track.isIdentified().then((isIdentified) => {
                        if (isIdentified) {
                            Track.logout();
                            if (toParams.siteId) {
                                $rootScope.$broadcast('permissionRequired', toParams);
                                return;
                            }

                            const redirectUrl = $location.path() + document.location.search;
                            defaultPermissionRequiredBehaviour(redirectUrl);
                        } else {
                            const isTargetSiteDemo = isDemoSiteId(toParams.siteId);
                            if (isTargetSiteDemo) {
                                redirectToDemoLogin();
                                return;
                            }
                            $rootScope.$broadcast('permissionRequired', toParams);
                        }
                    });
                }
                return;
            }

            const currentSite = UserData.currentSite;

            if (toState.name === 'react') {
                return;
            }

            /**
             * Note for the Future Migration
             * If following function should be migrated in the future, `useAuthorization` and `usePermission` hooks
             * can be used.
             *
             * For more information: https://hotjar.atlassian.net/wiki/spaces/DEV/pages/2924412984/Authorisation
             * @param accessLevel
             * @param requiredAccess
             * @param requiresAccountAdminAccess
             * @returns {boolean|*}
             */
            function hasRequiredAccess(accessLevel, requiredAccess, requiresAccountAdminAccess) {
                var accessLevels = ['none', 'read', 'write', 'admin'],
                    hasAccessLevel = accessLevels.indexOf(accessLevel) >= accessLevels.indexOf(requiredAccess),
                    user = User.get();

                return hasAccessLevel || (requiresAccountAdminAccess && user.flags.isAccountAdmin);
            }

            var toSiteId = parseInt(toParams.siteId, 10);
            var requiredAccess = toState.requiredAccess || 'read';
            var requiresAccountAdminAccess = toState.requiresAccountAdminAccess || false;

            // If the user does not have a current site, or is transitioning to a different site,
            // then the currentSite needs updating/to be set.
            if (toState.name && (!currentSite || (toSiteId && currentSite.id !== toSiteId))) {
                event.preventDefault();

                UserData.getResources().then(function (resources = {}) {
                    const { sites, organizations } = resources;
                    var site;

                    // The user can have no organization in the following cases:
                    // - their access was revoked,
                    // - they haven't completed the onboarding.
                    // In any of this cases we should let them complete the onboarding and create the site.
                    if (_.isEmpty(organizations)) {
                        browserHistory.push(getRoute(ONBOARDING.path, {}));
                        return;
                    }

                    // The user can have access to only 1 organization with no sites if they got
                    // invited to an empty organization before we pushed the change to prevent this
                    if (_.isEmpty(sites)) {
                        browserHistory.push(getRoute(NO_SITE_ACCESS.path, {}));

                        return;
                    }

                    if (toSiteId) {
                        site = _.find(sites, { id: toSiteId });
                    }
                    // The requested site might not accessible by the user,
                    // therefore set the first site or show an error.
                    if (!site) {
                        if (
                            toParams.siteId !== undefined &&
                            toParams.siteId !== '' &&
                            toParams.siteId !== '*' &&
                            $state.current.name !== 'settings.account'
                        ) {
                            $rootScope.$broadcast('permissionRequired', toParams);
                            return;
                        } else {
                            site = sites[0];
                        }
                    }
                    UserData.set('currentSite', site);
                    toParams.siteId = site.id;

                    if (!hasRequiredAccess(site.organization.access, requiredAccess, requiresAccountAdminAccess)) {
                        $state.go('403');
                        return;
                    }
                    $state.go(toState.name, toParams, { reload: true });
                }, restDefaultHandlers.onError);
            } else {
                // It could be that a 'toParams.siteId' has not been set (e.g. $state.go('surveys.create')).
                // Ensure that this is set, in order for the URL to direct to the current site.
                if (!toSiteId) {
                    if (
                        toParams.siteId !== undefined &&
                        toParams.siteId !== '' &&
                        toParams.siteId !== '*' &&
                        $state.current.name !== 'settings.account'
                    ) {
                        event.preventDefault();
                        toParams.siteId = currentSite.id;
                        browserHistory.replace(getRoute(SITE.LIST.path));
                        Notify.error("You don't have access to the requested site.");
                        return;
                    } else {
                        toParams.siteId = currentSite.id;
                    }
                }

                if (!hasRequiredAccess(currentSite.organization.access, requiredAccess, requiresAccountAdminAccess)) {
                    event.preventDefault();
                    $state.go('403');
                    return;
                }
            }
        });

        function showPermissionRequiredPage(siteId, next) {
            $state.go('permission-required', { siteId, next });
        }

        function defaultPermissionRequiredBehaviour(next) {
            if (UserData.loggedIn) {
                browserHistory.replace(getRoute(SITE.LIST.path));
            } else {
                browserHistory.replace(getTargetUrl(LOGIN.path, next).toString());
            }
            Notify.error("You don't have access to the requested site.");
        }

        $rootScope.$on('permissionRequired', (event, toParams) => {
            const siteId = toParams?.siteId;
            const redirectUrl = $location.path() + document.location.search;
            if (!siteId) {
                defaultPermissionRequiredBehaviour(redirectUrl);
            } else {
                showPermissionRequiredPage(siteId, redirectUrl);
            }
        });
    });
